import { useApolloClient, useSubscription } from "@apollo/client/react";
import { readFragment, type VariablesOf } from "gql.tada";

import {
  JOURNAL_ENTRIES_QUERY,
  JOURNAL_ENTRY_ADDED_SUBSCRIPTION,
  JournalEntrySummaryFragment,
} from "../JournalFeed.graphql";

/**
 * Filters input shape, derived from the subscription's variables to stay in
 * sync with the GraphQL schema. We export it so consumers (the feed,
 * filter bar) share a single source of truth.
 */
export type JournalFiltersInput = NonNullable<
  VariablesOf<typeof JOURNAL_ENTRY_ADDED_SUBSCRIPTION>["filters"]
>;

/**
 * Subscribes to `journalEntryAdded` for the given filters and prepends each
 * incoming entry into the cached `JOURNAL_ENTRIES_QUERY` result so the feed
 * updates live without a refetch.
 *
 * The hook is a no-op when the cache has no entry for the current filter set
 * (e.g. the feed query has not yet completed) — the next `useQuery` cycle
 * will produce a fresh page and subsequent entries will be merged.
 *
 * `useSubscription` automatically tears down the underlying WebSocket
 * subscription when the component unmounts or when `variables` change, so
 * no explicit cleanup is needed here.
 */
export function useJournalLiveTail(filters: JournalFiltersInput): void {
  const client = useApolloClient();

  useSubscription(JOURNAL_ENTRY_ADDED_SUBSCRIPTION, {
    variables: { filters },
    onData: ({ data }) => {
      const entry = data.data?.journalEntryAdded;
      if (!entry) return;

      const existing = client.readQuery({
        query: JOURNAL_ENTRIES_QUERY,
        variables: { filters },
      });
      if (!existing) return;

      const existingEdges = existing.journalEntries.edges;
      const { id: entryId } = readFragment(JournalEntrySummaryFragment, entry);

      const alreadyPresent = existingEdges.some((edge) => {
        const { id } = readFragment(JournalEntrySummaryFragment, edge.node);
        return id === entryId;
      });
      if (alreadyPresent) return;

      // Synthetic cursor — never used as a pagination boundary because the
      // live edge sits before the first server-issued edge.
      const newEdge = {
        __typename: "JournalEntryEdge" as const,
        cursor: `live:${entryId}`,
        node: entry,
      };

      client.writeQuery({
        query: JOURNAL_ENTRIES_QUERY,
        variables: { filters },
        data: {
          journalEntries: {
            ...existing.journalEntries,
            edges: [newEdge, ...existingEdges],
          },
        },
      });
    },
  });
}
